import React, { useEffect, useState } from "react";
import { Form, Input, Select, DatePicker, InputNumber, Button, Modal, Row, Col, Space, Divider, Tooltip, Radio, Card, Typography } from "antd";
import { QuestionCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useUld } from "../context/UldContext";
import { ULD_IDENTIFIER_REGEX, ULD_IDENTIFIER_FORMAT_HINT, ULD_TYPE_OPTIONS, CREATE_MODE_OPTIONS } from "../constants/uldConstants";
import { toCreateUldInitialStatusFields } from "../../lib/createUldInitialStatus";
import { NEW_ULD_CORE_STATUS_OPTIONS } from "../constants/uldConstantsV3";
import dayjs from "dayjs";

const { Text } = Typography;

const UldForm = () => {
    const [form] = Form.useForm();
    const { formVisible, closeForm, createUld, updateUld, editingUld, saving, tagList } = useUld();
    const [createMode, setCreateMode] = useState("single");
    const [ownershipType, setOwnershipType] = useState("自有");

    // 当编辑对象变化时重置表单
    useEffect(() => {
        if (formVisible) {
            form.resetFields();

            if (editingUld) {
                // 编辑模式：只支持单个模式
                setCreateMode("single");
                setOwnershipType(editingUld.ownership_type || "自有");
                // 处理日期字段，将日期字符串转换为dayjs对象（仅日期部分，避免时区问题）
                const formValues = {
                    mode: "single",
                    ...editingUld,
                    manufacture_date: editingUld.manufacture_date ? dayjs(editingUld.manufacture_date.split('T')[0]) : null,
                    purchase_date: editingUld.purchase_date ? dayjs(editingUld.purchase_date.split('T')[0]) : null,
                    lease_start_date: editingUld.lease_start_date ? dayjs(editingUld.lease_start_date.split('T')[0]) : null,
                    lease_end_date: editingUld.lease_end_date ? dayjs(editingUld.lease_end_date.split('T')[0]) : null,
                };
                form.setFieldsValue(formValues);
            } else {
                // 新建模式：重置为单个模式
                setCreateMode("single");
                setOwnershipType("自有");
                form.setFieldsValue({
                    mode: "single",
                    initial_core_status: "IN_STOCK", // 默认核心状态
                    ownership_type: "自有",
                });
            }
        }
    }, [form, formVisible, editingUld]);

    // 提交表单
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            // 处理日期字段，确保符合后端Pydantic datetime格式要求
            // 对于纯日期字段，需要添加时间部分 (YYYY-MM-DDTHH:mm:ss)
            if (values.manufacture_date) {
                values.manufacture_date = dayjs(values.manufacture_date).format('YYYY-MM-DDTHH:mm:ss');
            }
            if (values.purchase_date) {
                values.purchase_date = dayjs(values.purchase_date).format('YYYY-MM-DDTHH:mm:ss');
            }
            if (values.lease_start_date) {
                values.lease_start_date = dayjs(values.lease_start_date).format('YYYY-MM-DDTHH:mm:ss');
            }
            if (values.lease_end_date) {
                values.lease_end_date = dayjs(values.lease_end_date).format('YYYY-MM-DDTHH:mm:ss');
            }

            // 处理JSON字段 dimensions_cm（仅单个模式）
            if (values.mode === "single" && values.dimensions_cm) {
                const { length, width, height } = values.dimensions_cm;
                // 检查是否所有维度都有有效正数值
                const hasValidLength = length !== undefined && length !== null && length > 0;
                const hasValidWidth = width !== undefined && width !== null && width > 0;
                const hasValidHeight = height !== undefined && height !== null && height > 0;

                if (hasValidLength && hasValidWidth && hasValidHeight) {
                    values.dimensions_cm = { length, width, height };
                } else if (length === undefined && width === undefined && height === undefined) {
                    // 如果所有维度都未定义（即用户根本没填），则删除该字段
                    delete values.dimensions_cm;
                } else {
                    // 如果用户填写了部分维度，但不是全部有效，或者填写了非正数
                    form.setFields([
                        {
                            name: ["dimensions_cm", "length"],
                            errors: ["长度必须为正数"],
                        },
                        {
                            name: ["dimensions_cm", "width"],
                            errors: ["宽度必须为正数"],
                        },
                        {
                            name: ["dimensions_cm", "height"],
                            errors: ["高度必须为正数"],
                        },
                    ]);
                    return; // 阻止提交
                }
            } else {
                // 批量模式下删除dimensions_cm字段
                delete values.dimensions_cm;
            }

            // 处理核心状态转换为IATA状态（V3模型适配）
            if (values.initial_core_status) {
                const statusFields = toCreateUldInitialStatusFields(values.initial_core_status);
                values.initial_status = statusFields.initial_status;
                // 丢失需要后端把核心状态写成 LOST（补录已经找不到的板），透传给后端；
                // 其它状态维持现状（后端按入库位置默认在库）
                if (statusFields.initial_core_status) {
                    values.initial_core_status = statusFields.initial_core_status;
                } else {
                    delete values.initial_core_status;
                }
            }

            // 处理批量模式的ULD识别码列表
            if (values.mode === "batch" && values.uld_identifiers_text) {
                // 解析多行文本为数组
                const identifiers = values.uld_identifiers_text
                    .split('\n')
                    .map(line => line.trim())
                    .filter(line => line.length > 0);
                
                values.uld_identifiers = identifiers;
                delete values.uld_identifiers_text;
            }

            if (editingUld) {
                await updateUld(editingUld.id, values);
            } else {
                await createUld(values);
            }
        } catch (error) {
            console.error("表单验证失败:", error);
        }
    };

    // 处理取消
    const handleCancel = () => {
        closeForm();
    };

    // 处理创建模式变化
    const handleModeChange = (e) => {
        const newMode = e.target.value;
        setCreateMode(newMode);
        
        // 更新表单中的mode字段
        form.setFieldValue("mode", newMode);
        
        // 清除相关字段
        if (newMode === "single") {
            form.setFieldsValue({
                uld_identifiers_text: undefined,
            });
        } else {
            form.setFieldsValue({
                uld_identifier: undefined,
                uld_type: undefined,
                serial_number: undefined,
                // 清除详细制造信息（批量模式不支持）
                manufacturer: undefined,
                manufacture_date: undefined,
                purchase_date: undefined,
                purchase_cost: undefined,
                tare_weight_kg: undefined,
                max_gross_weight_kg: undefined,
                dimensions_cm: undefined,
                certification_status: undefined,
            });
        }
    };

    // 校验ULD识别码格式（简化版本，不再解析所有者代码）
    const validateUldIdentifier = (_, value) => {
        if (!value) {
            return Promise.reject(new Error("请输入ULD识别码"));
        }

        if (value.length !== 10) {
            return Promise.reject(new Error("ULD识别码长度必须为10位"));
        }

        if (!ULD_IDENTIFIER_REGEX.test(value)) {
            return Promise.reject(new Error("ULD识别码格式不正确，应为类型(3位)+序列号(7位)，总长度10位"));
        }

        // 提取ULD类型（前3位）和序列号（后7位，包含原序列号和所有者代码）
        const type = value.substring(0, 3);
        const serialNumber = value.substring(3); // 后7位作为序列号

        // 更新表单中的分解字段
        form.setFieldsValue({
            uld_type: type,
            serial_number: serialNumber,
        });

        return Promise.resolve();
    };

    // 处理ULD识别码变化（简化版本）
    const handleUldIdentifierChange = (e) => {
        const value = e.target.value;
        if (value.length === 10 && ULD_IDENTIFIER_REGEX.test(value)) {
            const type = value.substring(0, 3);
            const serialNumber = value.substring(3); // 后7位作为序列号
            
                form.setFieldsValue({
                    uld_type: type,
                    serial_number: serialNumber,
                });
        }
    };

    // 处理所有权类型变化
    const handleOwnershipTypeChange = (value) => {
        setOwnershipType(value);
        // 如果变更为非租赁类型，清空租赁日期字段
        if (value !== "租赁") {
            form.setFieldsValue({
                lease_start_date: undefined,
                lease_end_date: undefined,
            });
        }
    };

    return (
        <Modal
            title={editingUld ? "编辑ULD" : "创建ULD"}
            open={formVisible}
            width={900}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    取消
                </Button>,
                <Button key="submit" type="primary" loading={saving} onClick={handleSubmit}>
                    {editingUld ? "保存" : (createMode === "single" ? "创建" : "批量创建")}
                </Button>,
            ]}
        >
            <Form
                form={form}
                layout="vertical"
                name="uldForm"
                initialValues={{
                    mode: "single",
                    uld_identifier: "",
                    uld_type: "",
                    serial_number: "",
                    initial_core_status: "IN_STOCK",
                    owner: "",
                    holder: "",
                    ownership_type: "自有",
                    gha: "",
                }}
            >
                {/* 隐藏的mode字段 */}
                <Form.Item name="mode" style={{ display: 'none' }}>
                    <Input />
                </Form.Item>

                {/* 创建模式选择（仅新建时显示） */}
                {!editingUld && (
                    <Card size="small" style={{ marginBottom: 16 }}>
                        <Row>
                            <Col span={24}>
                                <div style={{ marginBottom: 8 }}>
                                    <InfoCircleOutlined style={{ marginRight: 4, color: '#1890ff' }} />
                                    创建模式
                                </div>
                                <Radio.Group value={createMode} onChange={handleModeChange}>
                                    {CREATE_MODE_OPTIONS.map(option => (
                                        <Radio key={option.value} value={option.value}>
                                            {option.label}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                            </Col>
                        </Row>
                    </Card>
                )}

                {/* 入库信息（新建时必填） */}
                {!editingUld && (
                    <>
                        <Divider orientation="left">入库信息</Divider>
                        <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f6f8fa', borderRadius: 6 }}>
                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                <InfoCircleOutlined style={{ marginRight: 4, color: '#1890ff' }} />
                                选择核心状态后，系统将自动设置对应的可用性和物理状态标识
                            </Text>
                        </div>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item 
                                    name="airport_iata" 
                                    label="入库机场"
                                    rules={[{ required: true, message: "请输入机场IATA三字码" }]}
                                >
                                    <Input placeholder="例如: PEK" maxLength={3} style={{ textTransform: 'uppercase' }} />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item 
                                    name="initial_core_status" 
                                    label={
                                        <span>
                                            初始核心状态
                                            <Tooltip title="选择ULD的初始核心状态。在库→可用且正常入库，在途→可用但已分配，停用→不可服务需维修，退役→永久停止使用">
                                                <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                                            </Tooltip>
                                        </span>
                                    }
                                    rules={[{ required: true, message: "请选择初始核心状态" }]}
                                >
                                    <Select placeholder="选择ULD的初始核心状态" options={NEW_ULD_CORE_STATUS_OPTIONS} />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="initial_tags" label="初始标签">
                                    <Select 
                                        mode="tags" 
                                        placeholder="选择或输入标签"
                                        style={{ width: '100%' }}
                                        options={tagList.map(tag => ({
                                            value: tag.tag_name,
                                            label: (
                                                <Space>
                                                    <span 
                                                        style={{
                                                            display: 'inline-block',
                                                            width: '8px',
                                                            height: '8px',
                                                            backgroundColor: tag.color_code,
                                                            borderRadius: '50%',
                                                        }}
                                                    />
                                                    {tag.tag_name}
                                                </Space>
                                            )
                                        }))}
                                        filterOption={(input, option) =>
                                            option.value.toLowerCase().includes(input.toLowerCase())
                                        }
                                        notFoundContent={tagList.length === 0 ? "暂无标签，请先创建标签" : "未找到匹配的标签"}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        
                        {/* 新增字段：所有者、持有者、所有权类型和GHA */}
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item 
                                    name="owner" 
                                    label="所有者（法律拥有者）"
                                    rules={[{ required: true, message: "请输入ULD所有者" }]}
                                >
                                    <Input placeholder="例如: 中国国际航空股份有限公司" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item 
                                    name="holder" 
                                    label="持有者（当前实际占有者）"
                                >
                                    <Input placeholder="例如: 中国东方航空股份有限公司" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item 
                                    name="ownership_type" 
                                    label="所有权类型"
                                    rules={[{ required: true, message: "请选择所有权类型" }]}
                                    initialValue="自有"
                                >
                                    <Select placeholder="选择所有权类型" onChange={handleOwnershipTypeChange}>
                                        <Select.Option value="自有">自有</Select.Option>
                                        <Select.Option value="租赁">租赁</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item 
                                    name="gha" 
                                    label="地面处理代理"
                                >
                                    <Input placeholder="例如: 首都机场地面服务有限公司" />
                                </Form.Item>
                            </Col>
                        </Row>
                        
                        {/* 租赁日期字段（仅当所有权类型为租赁时显示） */}
                        {ownershipType === "租赁" && (
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item 
                                        name="lease_start_date" 
                                        label="租赁开始日期"
                                        rules={[{ required: true, message: "租赁类型必须填写租赁开始日期" }]}
                                    >
                                        <DatePicker 
                                            placeholder="选择租赁开始日期" 
                                            style={{ width: '100%' }}
                                            format="YYYY-MM-DD"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item 
                                        name="lease_end_date" 
                                        label="租赁结束日期"
                                        rules={[
                                            {
                                                validator: (_, value) => {
                                                    const startDate = form.getFieldValue('lease_start_date');
                                                    if (value && startDate && value.isBefore(startDate)) {
                                                        return Promise.reject(new Error("租赁结束日期不能早于租赁开始日期"));
                                                    }
                                                    return Promise.resolve();
                                                }
                                            }
                                        ]}
                                    >
                                        <DatePicker 
                                            placeholder="选择租赁结束日期（可选）" 
                                            style={{ width: '100%' }}
                                            format="YYYY-MM-DD"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}
                    </>
                )}

                {/* 单个模式 - ULD基本信息 */}
                {createMode === "single" && (
                    <>
                        <Divider orientation="left">ULD基本信息</Divider>
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item
                            name="uld_identifier"
                            label={
                                <span>
                                    ULD识别码
                                    <Tooltip title={`${ULD_IDENTIFIER_FORMAT_HINT}。输入后系统会自动解析并填充ULD类型和序列号字段`}>
                                        <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                                    </Tooltip>
                                </span>
                            }
                            rules={[{ required: true, message: "请输入ULD识别码" }, { validator: validateUldIdentifier }]}
                        >
                            <Input 
                                placeholder="例如: AKE12345CA（输入后自动解析类型和序列号）" 
                                onChange={handleUldIdentifierChange} 
                                disabled={!!editingUld} 
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="uld_type" label="ULD类型" rules={[{ required: true, message: "请选择ULD类型" }]}>
                            <Select 
                                placeholder="从ULD识别码自动解析" 
                                options={ULD_TYPE_OPTIONS} 
                                disabled={true}
                                style={{ color: '#666' }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="serial_number" label="序列号" rules={[{ required: true, message: "请输入序列号" }]}>
                            <Input 
                                placeholder="从ULD识别码自动解析" 
                                disabled={true}
                                style={{ color: '#666' }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {/* 制造信息和物理属性已隐藏，根据新需求不再显示 */}
                    </>
                )}

                {/* 编辑模式 - 所有者信息字段 */}
                {editingUld && (
                    <>
                        <Divider orientation="left">所有者信息</Divider>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item 
                                    name="owner" 
                                    label="所有者（法律拥有者）"
                                    rules={[{ required: true, message: "请输入ULD所有者" }]}
                                >
                                    <Input placeholder="例如: 中国国际航空股份有限公司" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item 
                                    name="holder" 
                                    label="持有者（当前实际占有者）"
                                >
                                    <Input placeholder="例如: 中国东方航空股份有限公司" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item 
                                    name="ownership_type" 
                                    label="所有权类型"
                                    rules={[{ required: true, message: "请选择所有权类型" }]}
                                >
                                    <Select placeholder="选择所有权类型" onChange={handleOwnershipTypeChange}>
                                        <Select.Option value="自有">自有</Select.Option>
                                        <Select.Option value="租赁">租赁</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item 
                                    name="gha" 
                                    label="地面处理代理"
                                >
                                    <Input placeholder="例如: 首都机场地面服务有限公司" />
                                </Form.Item>
                            </Col>
                        </Row>
                        
                        {/* 租赁日期字段（仅当所有权类型为租赁时显示） */}
                        {ownershipType === "租赁" && (
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item 
                                        name="lease_start_date" 
                                        label="租赁开始日期"
                                        rules={[{ required: true, message: "租赁类型必须填写租赁开始日期" }]}
                                    >
                                        <DatePicker 
                                            placeholder="选择租赁开始日期" 
                                            style={{ width: '100%' }}
                                            format="YYYY-MM-DD"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item 
                                        name="lease_end_date" 
                                        label="租赁结束日期"
                                        rules={[
                                            {
                                                validator: (_, value) => {
                                                    const startDate = form.getFieldValue('lease_start_date');
                                                    if (value && startDate && value.isBefore(startDate)) {
                                                        return Promise.reject(new Error("租赁结束日期不能早于租赁开始日期"));
                                                    }
                                                    return Promise.resolve();
                                                }
                                            }
                                        ]}
                                    >
                                        <DatePicker 
                                            placeholder="选择租赁结束日期（可选）" 
                                            style={{ width: '100%' }}
                                            format="YYYY-MM-DD"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}
                    </>
                )}

                {/* 批量模式 - ULD识别码列表 */}
                {createMode === "batch" && !editingUld && (
                    <>
                        <Divider orientation="left">ULD识别码列表</Divider>
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    name="uld_identifiers_text"
                                    label={
                                        <span>
                                            ULD识别码列表
                                            <Tooltip title="每行输入一个ULD识别码，格式如 AKE12345CA">
                                                <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                                            </Tooltip>
                                        </span>
                                    }
                                    rules={[
                                        { required: true, message: "请输入ULD识别码列表" },
                                        {
                                            validator: (_, value) => {
                                                if (!value) return Promise.resolve();
                                                const lines = value.split('\n').filter(line => line.trim());
                                                if (lines.length === 0) {
                                                    return Promise.reject(new Error("请至少输入一个ULD识别码"));
                                                }
                                                if (lines.length > 100) {
                                                    return Promise.reject(new Error("一次最多支持100个ULD的批量创建"));
                                                }
                                                for (const line of lines) {
                                                    const identifier = line.trim();
                                                    if (identifier.length !== 10) {
                                                        return Promise.reject(new Error(`ULD识别码长度必须为10位: ${identifier}`));
                                                    }
                                                    if (!ULD_IDENTIFIER_REGEX.test(identifier)) {
                                                        return Promise.reject(new Error(`ULD识别码格式不正确: ${identifier}`));
                                                    }
                                                }
                                                return Promise.resolve();
                                            }
                                        }
                                    ]}
                                >
                                    <Input.TextArea 
                                        rows={8} 
                                        placeholder="请输入ULD识别码，每行一个，例如：&#10;AKE12345CA&#10;AKH67890YY&#10;PMC54321ZZ"
                                        style={{ fontFamily: 'monospace' }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </>
                )}

                {/* 备注信息 */}
                <Divider orientation="left">备注信息</Divider>
                <Row gutter={16}>
                    <Col span={24}>
                        <Form.Item name="remarks" label="备注">
                            <Input.TextArea 
                                rows={3} 
                                placeholder={createMode === "batch" ? "批量创建备注（将应用到所有ULD）" : "输入备注信息"} 
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default UldForm;
